/**
 * The renderer's shadow pass. three r187 has no hook between its shadow pass and its draw
 * (`WebGLRenderer.js:1750-1765`), so the pass runs three's pass, then Godot's copy of each omni cube
 * into its two paraboloids (`render_forward_clustered.cpp:2722-2738`), then each spot light and split
 * sun, then each area light's paraboloid (`atlasDepthPass.ts`), then each reader of the maps, and
 * last writes the sun shadow terms and records the framebuffer height.
 */
import { AtlasOmniShadow } from './positionalShadow/atlasOmniShadow.js';
import { AtlasSpotShadow } from './positionalShadow/atlasSpotShadow.js';
import { isAreaShadowLight } from './positionalShadow/atlasAreaShadow.js';
import { drawAreaShadows, drawProjectedShadows, } from './positionalShadow/atlasDepthPass.js';
import { DirectionalSplitShadow } from './directionalShadow/splitShadow.js';
import { writeFramebufferHeight } from './shadowFilter/framebufferRows.js';
import { writeSunShadowUniforms } from './directionalShadow/sunShadowUniforms.js';
import { OmniShadowCopy } from './positionalShadow/omniShadowCopy.js';
import { beginStep, endStep } from './frameProfile/frameProfile.js';
/**
 * A global key for a wrapped pass's readers, kept on the renderer's shadow map. A module that a dev
 * server evaluates again finds the pass already wrapped, and adds its readers to the set that pass
 * calls. Written only by `readersOf`, `readShadowMaps` and the disposer it returns.
 */
const READERS = Symbol.for('textscene.shadowPass');
/** The render step a frame profile times the readers as: the fog volumes are the only reader. */
const READ_STEP = 'Fog';
/**
 * A shared empty answer, so a pass with nothing to copy or draw allocates nothing. A pass that copies
 * builds its own list, not a shared one: each copy is a render, which runs this pass again inside it.
 */
const NO_LIGHTS = [];
/** Wraps `renderer`'s shadow pass once. A second call changes nothing. */
export function installShadowPass(renderer) {
    readersOf(renderer);
}
/**
 * Calls `read` after each shadow pass of `renderer` that renders `scene`, until the returned
 * disposer runs. The pass wraps itself on the first reader.
 */
export function readShadowMaps(renderer, scene, read) {
    const readers = readersOf(renderer);
    const reads = readers.get(scene) ?? new Set();
    readers.set(scene, reads.add(read));
    return () => {
        reads.delete(read);
        // A disposer that runs again after a new reader of `scene` must not drop that reader's set.
        if (reads.size === 0 && readers.get(scene) === reads)
            readers.delete(scene);
    };
}
/** The readers the wrapped pass of `renderer` calls, the pass wrapped on the first call. */
function readersOf(renderer) {
    const shadowMap = renderer.shadowMap;
    const existing = shadowMap[READERS];
    if (existing)
        return existing;
    const readers = new WeakMap();
    shadowMap[READERS] = readers;
    const threePass = { shadowMap, draw: shadowMap.render.bind(shadowMap) };
    const copy = new OmniShadowCopy();
    /**
     * True while the readers run. A reader's own render runs this pass again, and its time belongs to
     * the reader's step, so that pass marks no step of its own.
     */
    let isReading = false;
    shadowMap.render = (lights, scene, camera) => {
        const draws = passDraws(shadowMap);
        const copied = draws ? collected(lights, needsCopy) : NO_LIGHTS;
        const projected = draws ? collected(lights, isProjectedShadowLight) : NO_LIGHTS;
        const areas = draws ? collected(lights, isSlottedAreaLight) : NO_LIGHTS;
        // Three's pass draws no map with none to draw (r187 `WebGLShadowMap.js:94-97`).
        const isTimed = draws && lights.length > 0 && !isReading;
        if (isTimed)
            beginStep(renderer, 'Shadows');
        threePass.draw(lights, scene, camera);
        copyOmniShadows(renderer, copy, copied);
        drawProjectedShadows(threePass, projected, scene, camera);
        drawAreaShadows(threePass, areas, scene, camera);
        if (isTimed)
            endStep(renderer, 'Shadows');
        if (isReading) {
            readMaps(readers, scene, camera);
        }
        else {
            isReading = true;
            try {
                readMapsTimed(renderer, readers, scene, camera);
            }
            finally {
                isReading = false;
            }
        }
        // Last: each copy and each reader's render writes its own suns' terms and target's height.
        writeSunShadowUniforms(lights);
        writeFramebufferHeight(renderer);
    };
    return readers;
}
function readMaps(readers, scene, camera) {
    const reads = readers.get(scene);
    if (reads)
        for (const read of reads)
            read(camera);
}
/** {@link readMaps}, timed as {@link READ_STEP} when a reader reads `scene`. */
function readMapsTimed(renderer, readers, scene, camera) {
    if (!readers.has(scene))
        return;
    beginStep(renderer, READ_STEP);
    readMaps(readers, scene, camera);
    endStep(renderer, READ_STEP);
}
/** Copies each light's cube into the atlas. Each copy leaves the render target as it found it. */
function copyOmniShadows(renderer, copy, lights) {
    for (const light of lights) {
        copy.copy(renderer, light, light.shadow.slot);
        light.shadow.writeLookupMatrix(light);
    }
}
/**
 * Whether three's pass draws anything this time, read before the pass, which clears `needsUpdate`
 * (r187 `WebGLShadowMap.js:95`, `:170`, `:372`).
 */
function passDraws(shadowMap) {
    return shadowMap.enabled && (shadowMap.autoUpdate || shadowMap.needsUpdate);
}
/** The lights of one kind, without an array while there is none. */
function collected(lights, isKind) {
    let kind = null;
    for (const light of lights) {
        if (isKind(light))
            (kind ??= []).push(light);
    }
    return kind ?? NO_LIGHTS;
}
/** An omni light with a slot whose cube three's pass is about to draw. */
function needsCopy(light) {
    const { shadow } = light;
    return (shadow instanceof AtlasOmniShadow && shadow.slot !== null && (shadow.autoUpdate || shadow.needsUpdate));
}
/**
 * A split sun, or a spot light that holds a slot. three's pass never draws one by itself
 * (`splitShadow.ts`, `atlasSpotShadow.ts`).
 */
function isProjectedShadowLight(light) {
    const { shadow } = light;
    return (shadow instanceof DirectionalSplitShadow || (shadow instanceof AtlasSpotShadow && shadow.slot !== null));
}
/** An area light that holds a slot. three's pass never draws one by itself (`atlasAreaShadow.ts`). */
function isSlottedAreaLight(light) {
    return isAreaShadowLight(light) && light.shadow.slot !== null;
}
//# sourceMappingURL=shadowPass.js.map